import { DICTIONARY } from 'Enum';
import { Dictionary } from 'Dictionary';
import { CustomMetaTitle, LinkReturnToMain, NoDataFound2 } from 'Components';
import { useAppSelector, useDictionary, useIsDesktop } from 'Hooks';
import { Avatar, Button, Stack, Typography } from '@mui/material';
import { Title } from './Title';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLoader } from 'Hooks/useLoader';
import { CardsList } from './CardsList';
import { FilterTags } from './FilterTags';
import { PurpleArrowDown2 } from 'Images';
import { ILongtermEquipmentCard, getCards } from 'API/LongtermEquipment';
import {
  addMonths,
  differenceInDays,
  getYear,
  isAfter,
  isBefore,
  isWithinInterval,
} from 'date-fns';
import { MAX_PERMANENT_DATE_YEAR } from 'Config';

const MAX_ITEMS_TO_DISPLAY = 10;
const MAX_DAYS_DISPLAY_FUTURE = 30;
const NUMBER_OF_CARDS_TO_SHOW_FILTER = 6;

export const LongtermEquipment = () => {
  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(Dictionary, DICTIONARY.LongtermEquipment);
  const dictionaryUpdates = useDictionary(Dictionary, DICTIONARY.Updates);
  const [displayedUpdateCount, setDisplayedUpdateCount] =
    useState(MAX_ITEMS_TO_DISPLAY);
  const [cards, setCards] = useState<ILongtermEquipmentCard[]>([]);
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);
  const { isLoading, setIsLoading, loader } = useLoader();

  const imposterId = useAppSelector(
    ({ imposter: { imposterId } }) => imposterId
  );

  const FILTER_LABEL = {
    available: dictionary.filterLabels.available,
    availableSoon: dictionary.filterLabels.availableSoon,
  };

  const initializeData = useCallback(async () => {
    setIsLoading(true);
    const cards = await getCards();
    if (cards) {
      const dataWithLabels = cards.map((card: ILongtermEquipmentCard) => {
        const today = new Date();
        const isYear2999 =
          getYear(new Date(card.validToDate)) === MAX_PERMANENT_DATE_YEAR;

        const nextEntitleDate = new Date(card.nextEntitleDate);
        const lastRefStartDate = new Date(card.lastRefStartDate ?? '');

        const isPast = isBefore(nextEntitleDate, today);
        const isFuture =
          isAfter(nextEntitleDate, today) &&
          differenceInDays(nextEntitleDate, today) <= MAX_DAYS_DISPLAY_FUTURE;

        card.isPermanent = isYear2999;
        card.isAvailable = isPast;

        let filterLabel = '';
        if (isPast) filterLabel = FILTER_LABEL.available;
        else if (isFuture) filterLabel = FILTER_LABEL.availableSoon;

        // Important condition:
        // If the lastRefStartDate is after the nextEntitleDate, then the card is not available
        if (lastRefStartDate && isAfter(lastRefStartDate, nextEntitleDate)) {
          filterLabel = '';
          card.isAvailable = false;
        }

        return {
          ...card,
          filterLabel,
        };
      });

      // Sort the cards array by nextEntitleDate first
      const sortedByDate = dataWithLabels.sort((a, b) => {
        const dateA = new Date(a.nextEntitleDate);
        const dateB = new Date(b.nextEntitleDate);
        return dateA.getTime() - dateB.getTime();
      });

      // Then, move all the cards with FILTER_LABEL[1] to the top
      const availableCards = sortedByDate.filter(
        (card) => card.filterLabel === FILTER_LABEL.available
      );
      const futureCards = sortedByDate.filter(
        (card) => card.filterLabel === FILTER_LABEL.availableSoon
      );
      const otherCards = sortedByDate.filter((card) => card.filterLabel === '');

      const sortedCards = [...availableCards, ...futureCards, ...otherCards];

      setCards(sortedCards);
    }
    setIsLoading(false);
  }, [setIsLoading]);

  useEffect(() => {
    initializeData();
  }, [initializeData, imposterId]);

  const redirects = [
    {
      text: dictionaryUpdates.redirect4,
      href: '/equipment',
    },

    {
      text: dictionaryUpdates.redirect1,
      href: '/universalRequest',
    },
    { text: dictionaryUpdates.redirect2, href: '/compass-rights' },
  ];

  const filterBySubjects = (
    cards: ILongtermEquipmentCard[],
    selectedSubjects: string[]
  ): ILongtermEquipmentCard[] => {
    if (selectedSubjects.length === 0) return cards;

    const today = new Date();
    const filteredBySubjects: ILongtermEquipmentCard[] = [];
    for (const card of cards) {
      if (card.filterLabel && selectedSubjects.includes(card.filterLabel)) {
        if (card.filterLabel === FILTER_LABEL.available)
          filteredBySubjects.push(card);
        else if (
          card.filterLabel === FILTER_LABEL.availableSoon &&
          isWithinInterval(new Date(card.nextEntitleDate), {
            start: today,
            end: addMonths(today, 1),
          })
        )
          filteredBySubjects.push(card);
      }
    }
    return filteredBySubjects;
  };

  const subjects = useMemo(() => {
    const subjectNames = cards.map((card) => card.filterLabel).filter(Boolean);
    return [...new Set(subjectNames)];
  }, [cards]);

  const filteredUpdates = useMemo(() => {
    let filteredAndSortedUpdates = [...cards];

    if (selectedSubjects.length > 0) {
      filteredAndSortedUpdates = filterBySubjects(
        filteredAndSortedUpdates,
        selectedSubjects
      );
    }

    return filteredAndSortedUpdates?.slice(0, displayedUpdateCount);
  }, [cards, selectedSubjects, displayedUpdateCount]);

  const handleShowMore = () => {
    setDisplayedUpdateCount((prevCount) => prevCount + MAX_ITEMS_TO_DISPLAY);
  };

  return (
    <>
      <CustomMetaTitle title={dictionary.title} />
      <LinkReturnToMain link='/' />
      {isLoading ? (
        loader
      ) : (
        <>
          {cards.length > 0 ? (
            <Stack
              gap={isDesktop ? '28px' : '32px'}
              maxWidth={isDesktop ? '770px' : 'initial'}
            >
              <Title />
              {cards.length > NUMBER_OF_CARDS_TO_SHOW_FILTER && (
                <FilterTags
                  reset={() => setSelectedSubjects([])}
                  clearSelectedSubjects={() => setSelectedSubjects([])}
                  selectedSubjects={selectedSubjects}
                  setSelectedSubjects={setSelectedSubjects}
                  subjects={subjects}
                />
              )}
              <Typography variant='body3'>
                {dictionaryUpdates.filterCountResults(filteredUpdates.length)}
              </Typography>
              <Stack>
                <CardsList
                  data={filteredUpdates.slice(0, displayedUpdateCount)}
                />
              </Stack>
              {displayedUpdateCount <= filteredUpdates.length && (
                <Button
                  onClick={handleShowMore}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    m: '28px auto',
                    height: isDesktop ? '50px' : '30px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <Typography variant='body5'>{dictionary.showMore}</Typography>
                  <Avatar
                    alt=''
                    src={PurpleArrowDown2}
                    sx={{
                      height: '44px',
                      width: '50px',
                      ml: 1,
                    }}
                  />
                </Button>
              )}
            </Stack>
          ) : (
            <NoDataFound2
              gap={isDesktop ? '48px' : '32px'}
              redirects={redirects}
              headerComponent={
                <Stack alignItems={'center'} gap={'4px'}>
                  <Typography variant='h2' color='text.secondary'>
                    {dictionary.title}
                  </Typography>
                  <Typography
                    variant={'body3'}
                    color='text.secondary'
                    mt={'12px'}
                    maxWidth={isDesktop ? '260px' : '210px'}
                    textAlign={'center'}
                  >
                    {dictionary.noUpdatesSubtitle}
                  </Typography>
                </Stack>
              }
            />
          )}
        </>
      )}
    </>
  );
};
